import { Scale, Group } from '#utils/UNSAFE_visTypes/chart';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { styles } from '#PRIVATE_Chart/themes/ChartStyles.css';
import { VisTextStyle } from '#utils/UNSAFE_visUtils';
import { BarChartProps, BarItemData } from './barChart.types';
import { getStackLabelProps } from './utils/barItemLabelUtils';
import { Font } from '#hooks/PRIVATE_useTextDimensions/textDimensionsUtils';

type Props<K, D extends BarItemData<K>> = {
  groupCenter: number;
  labelStyle?: VisTextStyle;
  yScale: Scale;
  defaultFontSize: string;
  getStackLabel: NonNullable<BarChartProps<K, D>['getStackLabel']>;
  group: Group;
  isRtl: boolean;
  posValue: number;
  negValue: number;
  posItemsData: D[];
  isHoriz: boolean;
  negItemsData: D[];
  getTextDimensions: (text: string, font: Font) => Dimension;
};

export function StackLabels<K, D extends BarItemData<K>>({
  groupCenter,
  getStackLabel,
  group,
  posValue,
  negValue,
  posItemsData,
  yScale,
  isHoriz,
  isRtl,
  negItemsData,
  defaultFontSize,
  labelStyle,
  getTextDimensions
}: Props<K, D>) {
  const positiveStackLabel = getStackLabel({
    group,
    value: posValue,
    data: posItemsData
  });

  // default style used for text dims calc. should match with styles.stackLabelStyle
  const stackLabelStyle = { fontSize: defaultFontSize, fontWeight: 'bold', ...labelStyle };

  const negativeStackLabel =
    negValue < 0
      ? getStackLabel?.({
          group,
          value: negValue,
          data: negItemsData
        })
      : '';

  const posProps = getStackLabelProps(
    groupCenter,
    yScale.transform(posValue),
    false,
    isHoriz,
    isRtl,
    getTextDimensions(positiveStackLabel, stackLabelStyle)
  );
  const negProps = getStackLabelProps(
    groupCenter,
    yScale.transform(negValue),
    true,
    isHoriz,
    isRtl,
    getTextDimensions(negativeStackLabel, stackLabelStyle)
  );

  return (
    <>
      <text class={styles.stackLabelStyle} style={stackLabelStyle} {...posProps}>
        {positiveStackLabel}
      </text>
      <text class={styles.stackLabelStyle} style={stackLabelStyle} {...negProps}>
        {negativeStackLabel}
      </text>
    </>
  );
}
